import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, shareReplay } from 'rxjs';

import { ApiService } from './api.service';
import {
  CompanyResponse,
  InventoryItemResponse,
  ProjectResponse,
  ProjectTaskResponse,
  SampleShipmentResponse,
  UserLookupResponse
} from './models';

export type LookupSource =
  | 'companies'
  | 'inventoryItems'
  | 'projects'
  | 'projectTasks'
  | 'sampleShipments'
  | 'users'
  | 'workflowUsers';

export interface LookupOption {
  label: string;
  value: string;
  meta?: Record<string, unknown>;
}

export type LookupOptionMap = Record<LookupSource, LookupOption[]>;

@Injectable({ providedIn: 'root' })
export class LookupService {
  private readonly cache = new Map<LookupSource, Observable<LookupOption[]>>();

  constructor(private readonly api: ApiService) {}

  load(sources: LookupSource[]): Observable<LookupOptionMap> {
    const uniqueSources = [...new Set(sources)];
    if (uniqueSources.length === 0) {
      return of(emptyLookupOptionMap());
    }

    const requests: Partial<Record<LookupSource, Observable<LookupOption[]>>> = {};
    uniqueSources.forEach((source) => {
      requests[source] = this.options(source);
    });

    return forkJoin(requests).pipe(
      map((lookups) => ({
        ...emptyLookupOptionMap(),
        ...lookups
      }))
    );
  }

  refresh(sources: LookupSource[]): Observable<LookupOptionMap> {
    sources.forEach((source) => this.cache.delete(source));
    return this.load(sources);
  }

  private options(source: LookupSource): Observable<LookupOption[]> {
    const cached = this.cache.get(source);
    if (cached) {
      return cached;
    }

    const request = this.fetch(source).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.cache.set(source, request);
    return request;
  }

  private fetch(source: LookupSource): Observable<LookupOption[]> {
    switch (source) {
      case 'companies':
        return this.api.list<CompanyResponse>('/companies').pipe(
          map((companies) => companies.map((company) => option(company.id, company.companyName || company.id, companyLookupMeta(company)))),
          catchError(() => of([]))
        );
      case 'projects':
        return this.api.list<ProjectResponse>('/projects').pipe(
          map((projects) => projects.map((project) => option(project.id, project.projectName || project.id, projectLookupMeta(project)))),
          catchError(() => of([]))
        );
      case 'inventoryItems':
        return this.api.list<InventoryItemResponse>('/inventory-items').pipe(
          map((items) => items.map((item) => option(item.id, inventoryItemLabel(item), {
            availableQuantity: item.availableQuantity ?? 0,
            itemName: item.itemName,
            sku: item.sku
          }))),
          catchError(() => of([]))
        );
      case 'projectTasks':
        return this.api.list<ProjectTaskResponse>('/project-tasks').pipe(
          map((tasks) => tasks.map((task) => option(task.id, task.title || task.id))),
          catchError(() => of([]))
        );
      case 'sampleShipments':
        return this.api.list<SampleShipmentResponse>('/sample-shipments').pipe(
          map((shipments) => shipments.map((shipment) => option(shipment.id, shipmentLabel(shipment)))),
          catchError(() => of([]))
        );
      case 'users':
        return this.api.list<UserLookupResponse>('/users/lookup').pipe(
          map((users) => users.map((user) => option(user.id, userLabel(user)))),
          catchError(() => of([]))
        );
      case 'workflowUsers':
        return this.api.list<UserLookupResponse>('/users/workflow-lookup').pipe(
          map((users) => users.map((user) => option(user.id, userLabel(user)))),
          catchError(() => of([]))
        );
    }
  }
}

export function emptyLookupOptionMap(): LookupOptionMap {
  return {
    companies: [],
    inventoryItems: [],
    projects: [],
    projectTasks: [],
    sampleShipments: [],
    users: [],
    workflowUsers: []
  };
}

export function lookupFallbackLabel(source: LookupSource): string {
  switch (source) {
    case 'companies':
      return 'Unknown company';
    case 'inventoryItems':
      return 'Unknown inventory item';
    case 'projects':
      return 'Unknown project';
    case 'projectTasks':
      return 'Unknown task';
    case 'sampleShipments':
      return 'Unknown shipment';
    case 'users':
    case 'workflowUsers':
      return 'Unknown user';
  }
}

export function lookupOptionLabel(
  source: LookupSource,
  options: LookupOption[] | undefined | null,
  value: unknown,
  emptyFallback = '-'
): string {
  if (value === null || value === undefined || value === '') {
    return emptyFallback;
  }

  if (Array.isArray(value)) {
    const labels = value
      .map((entry) => lookupOptionLabel(source, options, entry, emptyFallback))
      .filter((entry) => entry !== emptyFallback);
    return labels.length > 0 ? labels.join(', ') : emptyFallback;
  }

  const text = String(value);
  return options?.find((option) => option.value === text)?.label ?? (isLikelyRecordId(text) ? lookupFallbackLabel(source) : text);
}

export function isLikelyRecordId(value: string): boolean {
  return /^[a-f0-9]{24}$/i.test(value.trim());
}

function inventoryItemLabel(item: InventoryItemResponse): string {
  const name = item.itemName || item.id;
  const sku = item.sku ? `${item.sku} - ` : '';
  const available = item.availableQuantity ?? 0;
  return `${sku}${name} (${available} available)`;
}

function companyLookupMeta(company: CompanyResponse): Record<string, unknown> | undefined {
  const shippingAddress = companyShippingAddressMeta(company);
  return shippingAddress ? { shippingAddress } : undefined;
}

function projectLookupMeta(project: ProjectResponse): Record<string, unknown> | undefined {
  return project.companyId ? { companyId: project.companyId } : undefined;
}

function companyShippingAddressMeta(company: CompanyResponse): Record<string, unknown> | undefined {
  const streetLines = [company.shippingStreetLine1, company.shippingStreetLine2].filter(Boolean);
  const hasShippingAddress = streetLines.length > 0
    || Boolean(company.shippingCity)
    || Boolean(company.shippingStateOrProvinceCode)
    || Boolean(company.shippingPostalCode);

  if (!hasShippingAddress) {
    return undefined;
  }

  return {
    contactName: company.shippingContactName || company.contactPersonName,
    companyName: company.companyName,
    phone: company.shippingContactPhone || company.contactPersonPhone,
    email: company.shippingContactEmail || company.contactPersonEmail,
    streetLines,
    city: company.shippingCity,
    stateOrProvinceCode: company.shippingStateOrProvinceCode,
    postalCode: company.shippingPostalCode,
    countryCode: company.shippingCountryCode || 'US',
    residential: company.shippingResidential ?? false
  };
}

function shipmentLabel(shipment: SampleShipmentResponse): string {
  const trackingNumber = shipment.trackingNumber?.trim();
  const recipient = shipment.recipientContactName?.trim();
  const carrier = shipment.carrier?.trim();

  if (trackingNumber && recipient) {
    return `${trackingNumber} - ${recipient}`;
  }
  if (trackingNumber) {
    return trackingNumber;
  }
  if (recipient && carrier) {
    return `${carrier} shipment to ${recipient}`;
  }
  if (recipient) {
    return `Shipment to ${recipient}`;
  }
  return `Shipment ${shortId(shipment.id)}`;
}

function option(value: string, label: string, meta?: Record<string, unknown>): LookupOption {
  return { value, label, meta };
}

function userLabel(user: UserLookupResponse): string {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return fullName ? `${fullName} (${user.username})` : user.username;
}

function shortId(value: string): string {
  return value.length > 6 ? value.slice(-6) : value;
}
